import { TmBaseComponent } from '@treasury/presentation';
import { closeIcon, editIcon, gripDotsIcon } from '@treasury/presentation/assets/icons';
import '@vaadin/grid';
// eslint-disable-next-line no-duplicate-imports
import { GridDragStartEvent, GridDropEvent } from '@vaadin/grid';
import { css, html } from 'lit';
import { columnBodyRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { type AccountGroup } from './configure-account-groups-sheet';

export const tagName = 'order-account-groups-table';

@customElement(tagName)
export class OrderAccountGroupsTable extends TmBaseComponent {
    @property({ type: Array })
    public accountGroups: AccountGroup[] = [];

    @state()
    private draggedItem?: AccountGroup;

    protected render() {
        if (this.accountGroups.length === 0) {
            return html`<div class="text-center text-secondary">No account groups configured</div>`;
        }
        return html`<vaadin-grid
            all-rows-visible
            rows-draggable
            .items=${this.accountGroups}
            .dropMode=${this.draggedItem ? 'between' : undefined}
            @grid-dragstart="${(event: GridDragStartEvent<AccountGroup>) => {
                [this.draggedItem] = event.detail.draggedItems;
            }}"
            @grid-dragend="${() => {
                this.draggedItem = undefined;
            }}"
            @grid-drop="${(event: GridDropEvent<AccountGroup>) => {
                const { dropTargetItem, dropLocation } = event.detail;
                // Only update when dropping on another item
                if (this.draggedItem && dropTargetItem !== this.draggedItem) {
                    const draggedItemIndex = this.accountGroups.indexOf(this.draggedItem);
                    this.accountGroups.splice(draggedItemIndex, 1);
                    const dropIndex =
                        this.accountGroups.indexOf(dropTargetItem) +
                        (dropLocation === 'below' ? 1 : 0);
                    this.accountGroups.splice(dropIndex, 0, this.draggedItem);
                    this.accountGroups = [...this.accountGroups];

                    this.dispatchEvent(
                        new CustomEvent('groups-reordered', {
                            detail: {
                                groups: this.accountGroups,
                                updatedGroup: this.draggedItem,
                                newIndex: dropIndex,
                            },
                        })
                    );
                }
            }}"
            ><vaadin-grid-column
                ${columnBodyRenderer(this.renderAccountGroup, [])}
            ></vaadin-grid-column
        ></vaadin-grid>`;
    }

    private renderAccountGroup(group: AccountGroup) {
        return html`<div class="bg-page py-1">
            <div class="flex justify-between bg-primary rounded-lg py-1.5 pl-4 pr-2 shadow">
                <div class="flex items-center">
                    <tm-icon .icon=${gripDotsIcon} color="#70757A"></tm-icon>
                </div>
                <div class="flex items-center grow px-4">
                    <span class="pr-2">${group.name}</span>
                    <span class="text-xs text-secondary">(${group.accounts.length})</span>
                    <tm-button
                        importance="tertiary"
                        class="ml-1"
                        @click=${() => {
                            this.dispatchEvent(
                                new CustomEvent('edit-group', {
                                    detail: group,
                                    bubbles: true,
                                    composed: true,
                                })
                            );
                        }}
                        ><tm-icon .icon=${editIcon}></tm-icon
                    ></tm-button>
                </div>
                <div class="flex items-center">
                    <tm-button
                        importance="tertiary"
                        @click=${() => {
                            const index = this.accountGroups.indexOf(group);
                            group.isRemoved = true;
                            this.accountGroups.splice(index, 1);
                            this.accountGroups = [...this.accountGroups];
                            this.dispatchEvent(
                                new CustomEvent('group-removed', {
                                    detail: { group, accountGroups: this.accountGroups },
                                })
                            );
                        }}
                        ><tm-icon .icon=${closeIcon} color="#70757A"></tm-icon
                    ></tm-button>
                </div>
            </div>
        </div>`;
    }

    public static get styles() {
        return [
            css`
                vaadin-grid-cell-content {
                    padding: 0;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: OrderAccountGroupsTable;
    }
}
